import React, { useEffect, useRef } from 'react';
import './ConfirmModal.css';

/**
 * Accessible confirmation / prompt modal.
 *
 * - role="dialog" + aria-modal, labelled by the title
 * - focuses the primary control on open, restores focus on close
 * - closes on Escape and on overlay click
 * - when `withInput` is true, acts as a text prompt and passes the value to onConfirm
 */
const ConfirmModal = ({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  danger = false,
  loading = false,
  withInput = false,
  inputLabel,
  inputPlaceholder = '',
  inputValue = '',
  onInputChange,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => {
  const dialogRef = useRef(null);
  const confirmRef = useRef(null);
  const inputRef = useRef(null);
  const previouslyFocused = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    previouslyFocused.current = document.activeElement;
    // Focus the input (prompt) or the confirm button (confirm).
    const toFocus = withInput ? inputRef.current : confirmRef.current;
    if (toFocus) toFocus.focus();

    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && !loading) {
        onCancel?.();
      }
    };
    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      // Restore focus to the element that opened the modal.
      if (previouslyFocused.current && previouslyFocused.current.focus) {
        previouslyFocused.current.focus();
      }
    };
  }, [open, withInput, loading, onCancel]);

  if (!open) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (confirmDisabled || loading) return;
    onConfirm?.(withInput ? inputValue : undefined);
  };

  return (
    <div
      className="confirm-overlay"
      onClick={() => !loading && onCancel?.()}
    >
      <div
        className="confirm-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-modal-title"
        ref={dialogRef}
        onClick={(e) => e.stopPropagation()}
      >
        <h3 id="confirm-modal-title" className="confirm-modal-title">{title}</h3>
        <form onSubmit={handleSubmit}>
          {message && <p className="confirm-modal-message">{message}</p>}

          {withInput && (
            <label className="confirm-modal-field">
              {inputLabel && <span>{inputLabel}</span>}
              <input
                ref={inputRef}
                type="text"
                value={inputValue}
                placeholder={inputPlaceholder}
                onChange={(e) => onInputChange?.(e.target.value)}
                disabled={loading}
              />
            </label>
          )}

          <div className="confirm-modal-actions">
            <button
              type="button"
              className="confirm-btn confirm-btn--cancel"
              onClick={() => onCancel?.()}
              disabled={loading}
            >
              {cancelLabel}
            </button>
            <button
              type="submit"
              ref={confirmRef}
              className={`confirm-btn ${danger ? 'confirm-btn--danger' : 'confirm-btn--primary'}`}
              disabled={loading || confirmDisabled}
            >
              {loading ? 'Working…' : confirmLabel}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ConfirmModal;
